// src/pages/DebtStudio/components/DealStructure/hooks/useDebtScheduleLogic.js
import dayjs from "lib/dayjs";
import {
  priceFromYield,
  safePrice,
} from "lib/bondMath";
import {
  dayCountFraction,
  freqToPeriodsPerYear,
} from "lib/dayCount";

/* ---------- Rate helpers ---------- */

/**
 * Interpret a value as a percent (what the user sees) and
 * convert to a decimal rate for math.
 *
 * Examples:
 *   "4"    -> 0.04
 *   "0.7"  -> 0.007
 *   ".7"   -> 0.007
 *   "5.13" -> 0.0513
 */
export function parsePercentOrDecimal(v) {
  if (v == null || v === "") return null;
  const s = String(v).trim().replace(/%/g, "");
  const n = Number(s);
  if (Number.isNaN(n)) return null;
  return n / 100;
}

/** Decimal (0.04) -> percent (4.000) */
function decimalToPercent(rateDec) {
  if (rateDec == null || !Number.isFinite(rateDec)) return null;
  return rateDec * 100;
}

export function percentDisplayValue(stored) {
  if (stored == null || stored === "") return "";
  if (typeof stored === "number" && Number.isFinite(stored)) {
    // stored is always literal percent now
    return stored.toFixed(3);
  }
  return String(stored);
}

/* ---------- recompute derived fields (flows) ---------- */
export function recomputeDerivedFields(inputRows, opts = {}) {
  const {
    couponFrequency = "semiannual",
    dayCount = "30/360",
    defaultCoupon = null,   // percent
    defaultYield = null,    // percent
    // Settlement date for pricing
    deliveryDate: deliveryDateIn = null,
    datedDate: datedDateLegacy = null,
  } = opts;

  if (!Array.isArray(inputRows)) return inputRows;

  const deliveryDate = deliveryDateIn ?? datedDateLegacy ?? null;
  const freqPeriods = freqToPeriodsPerYear(couponFrequency);

  // starting outstanding = explicit first-row outstanding OR sum of principals
  const startOutstanding =
    inputRows[0] && Number(inputRows[0].outstanding) > 0
      ? Number(inputRows[0].outstanding)
      : inputRows.reduce((s, rr) => s + Number(rr.principal ?? 0), 0);

  const out = [];
  const debugRows = [];
  let prevOutstanding = startOutstanding;

  for (let i = 0; i < inputRows.length; i++) {
    const r = { ...inputRows[i] };
    const principal = Number(r.principal ?? 0);

    // --- Coupon / Yield (normalize to decimals for math) ---
    // r.coupon and r.yield are always stored as percents
    let couponDecimal =
      r.coupon != null ? parsePercentOrDecimal(r.coupon) : null;
    if (couponDecimal == null && defaultCoupon != null) {
      couponDecimal = parsePercentOrDecimal(defaultCoupon);
    }

    let yieldDecimal =
      r.yield != null ? parsePercentOrDecimal(r.yield) : null;
    if (yieldDecimal == null && defaultYield != null) {
      yieldDecimal = parsePercentOrDecimal(defaultYield);
    }
    if (yieldDecimal == null && couponDecimal != null) {
      yieldDecimal = couponDecimal; // default to par if nothing provided
    }

    // --- Accrual logic for interest ---
    let accrualStart =
      r.accrual_start ??
      (i > 0 ? inputRows[i - 1]?.date : r.datedDate ?? r.dated_date);
    if (!accrualStart && r.date) {
      accrualStart = dayjs(r.date)
        .subtract(Math.round(12 / Math.max(1, freqPeriods)), "month")
        .format("YYYY-MM-DD");
    }
    const accrualEnd = r.date;

    let interestComputed = null;
    if (couponDecimal != null && accrualStart && accrualEnd) {
      const frac = dayCountFraction(accrualStart, accrualEnd, dayCount);
      interestComputed = principal * couponDecimal * frac;
    }

    const interest =
      r.interest != null && r.interest !== ""
        ? Number(r.interest)
        : Number.isFinite(interestComputed)
        ? interestComputed
        : 0;

    const total = Number((principal + interest).toFixed(2));
    const outstanding = Math.max(prevOutstanding, 0) - principal;
    prevOutstanding = outstanding;

    // --- Price calculation (initial per-row pass) ---
    // NOTE: For term bonds, this may be overwritten in the second pass.
    let price = null;
    if (couponDecimal != null && yieldDecimal != null && r.date && deliveryDate) {
      // Choose pricing date based on premium vs discount logic
      let pricingDate = r.date; // default to maturity
      const callDate = r.call_date ?? r.callDate ?? null;
      if (callDate && couponDecimal > yieldDecimal) {
        // Premium bond → price to call
        pricingDate = callDate;
      }

      const yrsSettleToMat = dayjs(pricingDate).isValid()
        ? dayCountFraction(deliveryDate, pricingDate, dayCount)
        : null;

      if (Number.isFinite(yrsSettleToMat) && yrsSettleToMat > 0) {
        price = priceFromYield({
          couponRate: couponDecimal,
          yieldRate: yieldDecimal,
          deliveryDate,
          maturityDate: pricingDate,
          dayCount,
          frequency: couponFrequency,
        });
      }
    }

    const fallbackPrice = Number.isFinite(price)
      ? price
      : Number.isFinite(r.price)
      ? Number(r.price)
      : 100;

    const finalPrice = safePrice(fallbackPrice);

    debugRows.push({
      rowDate: r.date,
      bondType: r.bond_type ?? null,
      termGroup: r.term_group ?? null,
      callDate: r.call_date ?? r.callDate ?? null,
      storedCoupon: r.coupon,
      storedYield: r.yield,
      couponDecimal,
      yieldDecimal,
      pricingSettlement: deliveryDate ?? null,
      dayCount,
      couponFrequency,
      priceComputed: price,
      fallbackPrice,
      finalPrice,
    });

    // Write back:
    // - coupon: percent with 3 decimals
    // - yield: percent with 3 decimals
    out.push({
      ...r,
      coupon:
        couponDecimal != null
          ? Number(decimalToPercent(couponDecimal).toFixed(3))
          : null,
      yield:
        yieldDecimal != null
          ? Number(decimalToPercent(yieldDecimal).toFixed(3))
          : null,
      interest: Number(interest.toFixed(2)),
      total: Number(total.toFixed(2)),
      outstanding: Number(outstanding.toFixed(2)),
      price: Number(finalPrice.toFixed(3)),
    });
  }

  // ---------- Term bond enforcement: single price for each term group ----------
  const termGroups = {};
  out.forEach((r) => {
    if (r.bond_type === "term" && r.term_group) {
      if (!termGroups[r.term_group]) termGroups[r.term_group] = [];
      termGroups[r.term_group].push(r);
    }
  });

  Object.values(termGroups).forEach((members) => {
    if (!members.length) return;
    members.sort((a, b) => (a.date < b.date ? -1 : 1));
    const last = members[members.length - 1];

    // last.coupon and last.yield are percents → convert to decimals
    const lastCouponDec = parsePercentOrDecimal(
      last.coupon ?? defaultCoupon
    );
    const lastYieldDec =
      parsePercentOrDecimal(last.yield ?? defaultYield) ??
      lastCouponDec;
    const lastCallDate = last.call_date ?? last.callDate ?? null;

    // Determine pricing date for the term (final maturity vs call if premium)
    let termPricingDate = last.date;
    if (
      lastCallDate &&
      lastCouponDec != null &&
      lastYieldDec != null &&
      lastCouponDec > lastYieldDec
    ) {
      termPricingDate = lastCallDate;
    }

    // Compute a single, shared term price (settlement always deliveryDate)
    let sharedTermPrice = Number.isFinite(last.price)
      ? Number(last.price)
      : 100;
    if (
      deliveryDate &&
      lastCouponDec != null &&
      lastYieldDec != null &&
      termPricingDate
    ) {
      const yrs = dayCountFraction(
        deliveryDate,
        termPricingDate,
        dayCount
      );
      if (Number.isFinite(yrs) && yrs > 0) {
        const px = priceFromYield({
          couponRate: lastCouponDec,
          yieldRate: lastYieldDec,
          deliveryDate,
          maturityDate: termPricingDate,
          dayCount,
          frequency: couponFrequency,
        });
        if (Number.isFinite(px)) sharedTermPrice = px;
      }
    }
    sharedTermPrice = Number(safePrice(sharedTermPrice).toFixed(3));

    // Apply to all members
    members.forEach((m) => {
      m.price = sharedTermPrice; // used by proceeds/solver
    });

    // UI cleanliness: only final row shows coupon/yield; others suppressed
    members.forEach((m) => {
      if (m !== last) {
        m.coupon = null;
        m.yield = null;
      }
    });

  });

  return out;
}
